import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Link,
  styled,
} from "@mui/material";
import { Icon } from "@iconify/react";

import { useStores } from "../../hooks/useStores";
import { useErrorReporter } from "../../hooks/use-error-reporter";

const ErrorDetails = styled(Box)(({ theme }) => ({
  overflowY: "auto",
  margin: "1rem",
  padding: "1rem",
  backgroundColor: theme.palette.grey.A100,
  borderRadius: 2,
  maxHeight: 200,
  fontSize: "0.8rem",
  color: theme.palette.grey.A400,
}));

const ErrorDisplay = observer(() => {
  const { sessionStore } = useStores();
  const [currentErrorIndex, setCurrentErrorIndex] = useState(0);
  const { activeErrors } = sessionStore;
  const { clearErrors } = useErrorReporter();

  if (activeErrors.length === 0) return null;

  const currentError = activeErrors[currentErrorIndex];
  const hasMultipleErrors = activeErrors.length > 1;

  const handlePrevious = () => {
    setCurrentErrorIndex((prev) =>
      prev > 0 ? prev - 1 : activeErrors.length - 1,
    );
  };

  const handleNext = () => {
    setCurrentErrorIndex((prev) =>
      prev < activeErrors.length - 1 ? prev + 1 : 0,
    );
  };

  const { errorText, errorObject } = currentError;

  const getErrorDetails = (error) => {
    if (!error) return null;

    // Axios error
    if (error.isAxiosError) {
      return (
        <ErrorDetails>
          Status: {error.response?.status || "Unknown"}
          <br />
          URL: {error.config?.url || "Unknown"}
          <br />
          Method: {error.config?.method?.toUpperCase() || "Unknown"}
          {error.response?.data && (
            <>
              <br />
              Response: {JSON.stringify(error.response.data, null, 2)}
            </>
          )}
        </ErrorDetails>
      );
    }

    // Apollo error
    if (error.networkError || error.graphQLErrors) {
      return (
        <ErrorDetails>
          {error.networkError && (
            <>
              Network Error: {error.networkError.message}
              <br />
            </>
          )}
          {error.graphQLErrors?.map((gqlError, index) => (
            <React.Fragment key={index}>
              GraphQL Error {index + 1}: {gqlError.message}
              <br />
            </React.Fragment>
          ))}
        </ErrorDetails>
      );
    }

    // Generic error
    if (error instanceof Error) {
      return <ErrorDetails>Error: {error.message}</ErrorDetails>;
    }

    // Error Boundary error
    if (error?.error instanceof Error && error?.errorInfo?.componentStack) {
      return (
        <ErrorDetails>
          Error: {error.error.message}
          <br />
          Component Stack:
          <br />
          {error.errorInfo.componentStack}
        </ErrorDetails>
      );
    }

    return null;
  };

  const anyFatalErrors = activeErrors.some((error) => error.fatal);

  return (
    <Dialog
      open={true}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: "4px solid #f44336",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Icon icon="mdi:alert-circle" color="#f44336" width="24" height="24" />
        <Typography variant="h6" component="span">
          There was a problem
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {errorText}
        </Typography>

        {getErrorDetails(errorObject)}

        {hasMultipleErrors && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 2,
            }}
          >
            <IconButton onClick={handlePrevious}>
              <Icon icon="mdi:chevron-left" width="24" height="24" />
            </IconButton>
            <Typography variant="body2" sx={{ mx: 2 }}>
              Error {currentErrorIndex + 1} of {activeErrors.length}
            </Typography>
            <IconButton onClick={handleNext}>
              <Icon icon="mdi:chevron-right" width="24" height="24" />
            </IconButton>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          If this error persists, please contact our{" "}
          <Link href="https://help.hatsandladders.com/" underline="hover">
            support team
          </Link>{" "}
          for assistance.
        </Typography>
      </DialogContent>
      {anyFatalErrors && (
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => (window.location.href = "/")}
          >
            Login
          </Button>
        </DialogActions>
      )}
      {anyFatalErrors || (
        <DialogActions>
          <Button variant="contained" color="primary" onClick={clearErrors}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
});

export default ErrorDisplay;
